import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

function GoogleButton() {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button data-testid="google-login-btn" type="button" variant="outline" onClick={onClick}
      className="w-full border-white/15 bg-transparent text-white hover:bg-white/5">
      <svg className="mr-1 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
      Continue with Google
    </Button>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center px-5 py-12">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(600px circle at 50% 0%, rgba(255,107,53,0.10), transparent 60%)" }} />
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center"><BrandMark /></div>
        <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-7 sm:p-9">
          <h1 className="font-head text-2xl font-extrabold tracking-tight text-white">{title}</h1>
          <p className="mt-1.5 text-sm text-neutral-400">{subtitle}</p>
          <div className="mt-7">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back.");
      navigate(u.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Log in" subtitle="One problem. One move. Let's get you your next one.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-neutral-300">Email</Label>
          <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 border-white/12 bg-[var(--surface-raised)] text-white" placeholder="you@company.com" />
        </div>
        <div>
          <Label className="text-neutral-300">Password</Label>
          <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 border-white/12 bg-[var(--surface-raised)] text-white" placeholder="••••••••" />
        </div>
        <Button data-testid="login-submit" type="submit" disabled={busy} className="btn-amber w-full">
          {busy ? "Signing in…" : "Log in"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-neutral-600">
        <div className="h-px flex-1 bg-white/10" /> OR <div className="h-px flex-1 bg-white/10" />
      </div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-neutral-400">
        New here? <Link to="/register" className="text-[var(--amber)] hover:underline">Create an account</Link>
      </p>
    </AuthShell>
  );
}
